// Ekstraøvelse: vælg 1–5 taster og få en blandet øvelse.

import { fromChunks, fromLetters, round } from '../data/curriculum/generators';
import type { GenerateRoundOptions, Trin, UserProfile } from '../types';
import { cumulativeWordPool } from './cumulativeWordPool';
import { getCumulativeUnlockedKeys, textUsesOnlyKeys } from './dictationUnits';

export const CUSTOM_PRACTICE_TRIN_ID = '__custom__';

export const CUSTOM_PRACTICE_MIN_KEYS = 1;
export const CUSTOM_PRACTICE_MAX_KEYS = 5;

/** Tegn der ikke giver mening i ord-diktat. */
export const CUSTOM_PRACTICE_NO_DICTATION_KEYS = new Set([',', '.', '-']);

export function isCustomPracticeEligible(user: UserProfile): boolean {
  return user.completedTierIds.includes('niveau-1');
}

/** Taster brugeren må vælge (alle låste op undtagen mellemrum). */
export function selectablePracticeKeys(user: UserProfile): string[] {
  return getCumulativeUnlockedKeys(user).filter((k) => k !== ' ');
}

export function sanitizeCustomPracticeKeys(
  user: UserProfile,
  keys: ReadonlyArray<string> | undefined,
): string[] {
  const allowed = new Set(selectablePracticeKeys(user));
  if (!keys?.length) return [];
  const out: string[] = [];
  for (const k of keys) {
    if (!allowed.has(k) || out.includes(k)) continue;
    out.push(k);
    if (out.length >= CUSTOM_PRACTICE_MAX_KEYS) break;
  }
  return out;
}

export function customPracticeAllowsDictation(keys: ReadonlyArray<string>): boolean {
  return !keys.some((k) => CUSTOM_PRACTICE_NO_DICTATION_KEYS.has(k));
}

function wordsForKeys(
  user: UserProfile,
  keys: ReadonlyArray<string>,
): string[] {
  const keySet = new Set(keys);
  if (keySet.size === 0) return [];

  return cumulativeWordPool(user)
    .map((word) => {
      let score = 0;
      const seen = new Set<string>();
      for (const ch of word) {
        if (!keySet.has(ch) || seen.has(ch)) continue;
        seen.add(ch);
        score += 1;
      }
      return { word, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ word }) => word);
}

function mixedCustomGenerate(
  keys: ReadonlyArray<string>,
  words: string[],
  length: number,
) {
  const letterGen = fromLetters([...keys], 2, Math.floor(length * 0.45));
  const wordGen = fromChunks(words.length > 0 ? words.slice(0, 48) : ['ord'], length);
  return (opts: GenerateRoundOptions) => {
    const letters = letterGen(opts);
    const chunk = wordGen(opts);
    return `${letters} ${chunk}`.trim();
  };
}

export function buildCustomPracticeShellTrin(user: UserProfile): Trin | null {
  if (!isCustomPracticeEligible(user)) return null;

  const unlockedKeys = getCumulativeUnlockedKeys(user);

  return {
    id: CUSTOM_PRACTICE_TRIN_ID,
    tierId: 'grundniveau',
    trinNumber: 0,
    title: 'Vælg selv',
    description: 'Vælg 1–5 taster du vil øve — øvelsen blandes efter dit valg.',
    goal: 'Styrk præcis de taster du selv vælger.',
    unlockedKeys,
    newKeys: [],
    rounds: [],
  };
}

export function buildCustomPracticeTrin(
  user: UserProfile,
  keys: ReadonlyArray<string>,
): Trin | null {
  if (!isCustomPracticeEligible(user)) return null;

  const focusKeys = sanitizeCustomPracticeKeys(user, keys);
  if (focusKeys.length < CUSTOM_PRACTICE_MIN_KEYS) return null;

  const unlockedKeys = getCumulativeUnlockedKeys(user);
  const words = wordsForKeys(user, focusKeys);
  const id = CUSTOM_PRACTICE_TRIN_ID;
  const label = focusKeys.map((k) => (k === ' ' ? 'mellemrum' : k)).join(', ');

  const rounds = [
    round({
      id: `${id}-r1`,
      title: 'Kombinationer',
      purpose: `Korte grupper med ${label}.`,
      mode: 'recall',
      generate: fromLetters(focusKeys, 2, 80),
    }),
  ];

  if (words.length > 0) {
    rounds.push(
      round({
        id: `${id}-r2`,
        title: 'Ord',
        purpose: 'Ord der bruger dine valgte taster.',
        mode: 'recall',
        generate: fromChunks(words.slice(0, 40), 70),
      }),
      round({
        id: `${id}-r3`,
        title: 'Blandet',
        purpose: 'Blanding af grupper og ord — hold flowet.',
        mode: 'recall',
        generate: mixedCustomGenerate(focusKeys, words, 90),
      }),
    );

    if (customPracticeAllowsDictation(focusKeys)) {
      const dictationWords = words
        .filter((w) => textUsesOnlyKeys(w, unlockedKeys))
        .slice(0, 36);
      if (dictationWords.length > 0) {
        rounds.push(
          round({
            id: `${id}-r4`,
            title: 'Diktat',
            purpose: 'Lyt til ordet og skriv det uden at se det.',
            mode: 'recall',
            generate: fromChunks(dictationWords, 55),
            presentation: { kind: 'dictation' },
          }),
        );
      }
    }
  }

  return {
    id,
    tierId: 'grundniveau',
    trinNumber: 0,
    title: 'Vælg selv',
    description: `Træner: ${label}.`,
    goal: 'Styrk præcis de taster du selv vælger.',
    unlockedKeys,
    newKeys: [],
    rounds,
  };
}
